import React, { useEffect, useMemo, useRef, useState } from "react";
import TerrainViewer from "../terrain3d/TerrainViewer";
import { LAYER_ORDER, layerLabel } from "../terrain3d/layerSpecs";
import { InfoTooltip } from "./BuilderUtils";

/**
 * LA CARTE DERRIÈRE UNE PAGE DE JEU (`page.map_view`).
 *
 * Une page déclare ce qu'elle veut de la carte de la séance ; elle ne la charge
 * pas. Le territoire et les couches construites appartiennent à l'atelier
 * (cf. territoryConfig.js) — ici on choisit seulement ce qui est ALLUMÉ, sous
 * quel angle, et à quel point on l'efface derrière le contenu.
 *
 * LE CADRAGE EST LIÉ À UN TERRITOIRE, ET C'EST LA SUBTILITÉ DE CET ÉCRAN. Une
 * pose de caméra est exprimée en coordonnées du terrain : celle qui cadre la
 * vallée du Rhône sur l'Ardèche ne désigne rien ailleurs. Or une page est
 * PARTAGÉE entre ateliers, qui peuvent porter des territoires différents. Le
 * territoire sur lequel le cadrage a été pris est donc enregistré avec lui, et
 * une séance qui joue ailleurs l'ignore plutôt que de survoler du vide.
 *
 * Corollaire assumé : l'aperçu ci-dessous demande de choisir un territoire, qui
 * n'est pas une propriété de la page mais le décor sur lequel on règle.
 */

// Les couches proposées viennent des datapacks réellement générés, pas d'une
// liste écrite ici : un territoire qui gagne une couche la propose aussitôt.
function layersOfTerritories(territories) {
  const seen = new Set();
  territories.forEach((t) => (t.layers || []).forEach((id) => seen.add(id)));
  return LAYER_ORDER.filter((id) => seen.has(id));
}

const DEFAULT_TREATMENT = { dim: 0.35, blur: 2, saturate: 0.85 };

export default function MapViewEditor({ value, onChange, territories = [], pageTerritory }) {
  const view = value || null;
  const mode = view?.mode || "off";
  const viewerRef = useRef(null);
  const [placing, setPlacing] = useState(false);

  // Territoire de composition. Il n'est un CHOIX que si la page n'appartient
  // encore à aucun atelier porteur de carte : sinon c'est l'atelier qui le
  // fixe, et proposer une liste n'ouvrirait qu'une occasion de se tromper.
  const derived = pageTerritory?.free ? null : pageTerritory?.territoryId || null;
  const [chosenTerritory, setChosenTerritory] = useState(view?.territory_id || territories[0]?.id || "");
  const previewTerritory = derived || chosenTerritory;
  useEffect(() => {
    if (!chosenTerritory && territories.length) setChosenTerritory(territories[0].id);
  }, [territories, chosenTerritory]);

  const availableLayers = useMemo(() => layersOfTerritories(territories), [territories]);
  const territoryLayers = territories.find((t) => t.id === previewTerritory)?.layers || [];

  const patch = (fields) => onChange({ ...(view || { mode: "backdrop" }), ...fields });

  const setMode = (next) => {
    if (next === "off") return onChange(null);
    const common = {
      mode: next,
      active_layers: view?.active_layers || ["rivers"],
      layer_settings: view?.layer_settings || {},
      camera: view?.camera || null,
      territory_id: view?.territory_id || null,
    };
    // Chaque mode ne porte que ce qui le concerne : une page localisée n'a ni
    // rotation ni voile (la carte n'est pas un fond, c'est l'écran), un fond de
    // carte n'a pas de repère.
    onChange(next === "located"
      ? { ...common, anchor: view?.anchor || null }
      : { ...common, motion: view?.motion || { orbit: 0.3 }, treatment: view?.treatment || DEFAULT_TREATMENT });
  };

  const toggleLayer = (id) => {
    const active = view?.active_layers || [];
    patch({ active_layers: active.includes(id) ? active.filter((l) => l !== id) : [...active, id] });
  };

  const capturePose = () => {
    const pose = viewerRef.current?.getCurrentPose();
    if (!pose) return;
    patch({ camera: pose, territory_id: previewTerritory });
  };

  // Le point se POSE SUR LA CARTE, il ne se saisit pas : une position en
  // coordonnées du terrain n'a aucun sens dans un formulaire. Même geste que
  // pour les zones d'intérêt des étapes d'intro.
  const placeAnchor = (position) => {
    patch({
      anchor: { ...(view?.anchor || {}), position },
      territory_id: previewTerritory,
    });
    setPlacing(false);
  };

  const isLocated = mode === "located";
  // Une page pas encore rattachée à un atelier reste localisable : c'est à
  // l'ajout dans une pile que la compatibilité sera vérifiée.
  const canLocate = pageTerritory ? pageTerritory.canLocate : true;

  // Aperçu du repère posé, avec le contenu qu'il portera en jeu — l'auteur doit
  // voir la fiche telle qu'elle sera projetée, pas une pastille nue.
  const anchorPreviewPoints = useMemo(() => {
    if (!isLocated || !view?.anchor?.position) return [];
    return [{
      point_id: "page-anchor",
      position: view.anchor.position,
      title: view.anchor.label || "Ici",
      image_url: "",
      text: "",
    }];
  }, [isLocated, view?.anchor?.position, view?.anchor?.label]);

  const treatment = view?.treatment || DEFAULT_TREATMENT;
  const setTreatment = (field, v) => patch({ treatment: { ...treatment, [field]: v } });
  const orbit = view?.motion?.orbit ?? 0;

  return (
    <div className="builder-mapview">
      <label className="builder-label" style={{ maxWidth: 380 }}>
        <span className="field-label-row">
          Carte du territoire
          <InfoTooltip text="Sans effet sur un atelier qui n'a pas déclaré de territoire : la page garde alors son fond habituel." />
        </span>
        <select className="builder-input" value={mode} onChange={(e) => setMode(e.target.value)}>
          <option value="off">— Aucune (fond habituel de la page) —</option>
          <option value="backdrop">Fond de carte</option>
          {/* LOCALISER EST UNE PROMESSE DE LIEU. Elle ne tient que si tous les
              ateliers qui emploient la page se déroulent sur le même terrain.
              L'option est donc retirée plutôt que proposée puis refusée à
              l'enregistrement — sauf si la page est DÉJÀ localisée, auquel cas
              il faut pouvoir revenir en arrière. */}
          <option value="located" disabled={!canLocate && !isLocated}>
            Page localisée (un lieu sur la carte){!canLocate && !isLocated ? " — indisponible" : ""}
          </option>
        </select>
        {!canLocate && (
          <span className="builder-hint builder-hint-error">
            {pageTerritory.blockReason}
            {isLocated
              ? " Dupliquez la page pour en avoir une version par territoire."
              : " Dupliquez-la si vous voulez la localiser ici."}
          </span>
        )}
      </label>

      {mode !== "off" && (
        <>
          <p className="builder-hint">
            {isLocated
              ? "La carte occupe tout l'écran, vole jusqu'au lieu et y pose un repère. Le titre, l'image et le texte de cette page s'affichent dans la fiche reliée au repère — ils ne sont donc pas à ressaisir ici."
              : "La carte est un décor : elle ne reçoit aucun clic et n'affiche ni légende ni fiche. Le fond habituel de la page (image, aplat) est masqué pendant ce temps."}
          </p>

          <div className="builder-mapview-grid">
            <div className="builder-mapview-controls">
              <span className="builder-label-text">Couches allumées</span>
              <div className="builder-mapview-layers">
                {availableLayers.map((id) => {
                  const on = (view?.active_layers || []).includes(id);
                  const absent = previewTerritory && !territoryLayers.includes(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      className={`builder-chip builder-chip-btn${on ? " is-on" : ""}`}
                      onClick={() => toggleLayer(id)}
                      title={absent ? `Absente du datapack « ${previewTerritory} »` : undefined}
                    >
                      {layerLabel(id)}{absent && " ⚠"}
                    </button>
                  );
                })}
              </div>

              {isLocated ? (
                <>
                  <span className="builder-label-text">Le lieu</span>
                  <button
                    type="button"
                    className={`builder-btn-secondary${placing ? " is-active" : ""}`}
                    onClick={() => setPlacing((v) => !v)}
                  >
                    {placing
                      ? "Cliquez sur la carte…"
                      : view?.anchor?.position ? "Déplacer le repère" : "Poser le repère"}
                  </button>
                  <label className="builder-label">
                    <span className="builder-hint">Nom du lieu (aide à la relecture)</span>
                    <input
                      className="builder-input"
                      value={view?.anchor?.label || ""}
                      placeholder="ex : Privas"
                      onChange={(e) => patch({ anchor: { ...(view?.anchor || {}), label: e.target.value } })}
                    />
                  </label>
                  {!view?.anchor?.position && (
                    <span className="builder-hint builder-hint-error">
                      Aucun repère posé : l'atelier refusera d'être enregistré.
                    </span>
                  )}
                </>
              ) : (
                <>
                  <label className="builder-label">
                    <span className="field-label-row">
                      Rotation
                      <InfoTooltip text="Vitesse de rotation automatique. 0 = carte fixe. C'est la rotation qui coûte des images par seconde : une carte fixe ne redessine presque rien." />
                    </span>
                    <input
                      type="range" min="0" max="1" step="0.05" value={orbit}
                      onChange={(e) => patch({ motion: { orbit: Number(e.target.value) } })}
                    />
                    <span className="builder-hint">{orbit === 0 ? "Carte fixe" : `${orbit.toFixed(2)} tour/min environ`}</span>
                  </label>

                  <span className="builder-label-text">Lisibilité du contenu par-dessus</span>
                  <label className="builder-label">
                    <span className="builder-hint">Assombrissement · {Math.round((treatment.dim ?? 0) * 100)} %</span>
                    <input type="range" min="0" max="0.9" step="0.05"
                      value={treatment.dim ?? 0}
                      onChange={(e) => setTreatment("dim", Number(e.target.value))} />
                  </label>
                  <label className="builder-label">
                    <span className="builder-hint">Flou · {treatment.blur ?? 0} px</span>
                    <input type="range" min="0" max="10" step="1"
                      value={treatment.blur ?? 0}
                      onChange={(e) => setTreatment("blur", Number(e.target.value))} />
                  </label>
                  <label className="builder-label">
                    <span className="builder-hint">Saturation · {Math.round((treatment.saturate ?? 1) * 100)} %</span>
                    <input type="range" min="0.2" max="1" step="0.05"
                      value={treatment.saturate ?? 1}
                      onChange={(e) => setTreatment("saturate", Number(e.target.value))} />
                  </label>
                </>
              )}
            </div>

            <div className="builder-mapview-preview">
              <div className="builder-mapview-previewhead">
                {/* Le territoire n'est un choix que tant que la page n'appartient
                    à aucun atelier porteur de carte. Ensuite il est imposé, et
                    seulement rappelé. */}
                {pageTerritory?.free ? (
                  <select
                    className="builder-input"
                    value={chosenTerritory}
                    onChange={(e) => setChosenTerritory(e.target.value)}
                    title="Décor de composition — la page n'est encore dans aucun atelier"
                  >
                    {territories.map((t) => (
                      <option key={t.id} value={t.id}>{t.label || t.id}</option>
                    ))}
                  </select>
                ) : (
                  <span className="builder-hint" style={{ flex: 1 }}>
                    Territoire imposé par l'atelier : <strong>{previewTerritory || "aucun"}</strong>
                  </span>
                )}
                <button type="button" className="builder-btn-secondary" onClick={capturePose}>
                  Capturer le cadrage
                </button>
                {view?.camera && (
                  <button
                    type="button"
                    className="builder-btn-secondary"
                    onClick={() => patch({ camera: null })}
                  >
                    Oublier
                  </button>
                )}
              </div>

              <div className={`builder-mapview-frame${placing ? " is-placing" : ""}`}>
                {previewTerritory ? (
                  <TerrainViewer
                    ref={viewerRef}
                    territoryId={previewTerritory}
                    availableLayers={territoryLayers}
                    activeLayers={view?.active_layers || []}
                    layerSettings={view?.layer_settings || {}}
                    cameraPose={view?.camera || null}
                    chrome={isLocated ? "markers" : "none"}
                    points={anchorPreviewPoints}
                    placementMode={placing}
                    onTerrainClick={placeAnchor}
                    fogEnabled
                  />
                ) : (
                  <div className="builder-empty">Aucun territoire disponible.</div>
                )}
                {!isLocated && (
                  <div
                    className="builder-mapview-veil"
                    style={{ opacity: treatment.dim ?? 0 }}
                    aria-hidden="true"
                  />
                )}
              </div>

              <span className="builder-hint">
                {!view?.camera
                  ? "Sans cadrage capturé, la carte reste là où la page précédente l'a laissée."
                  : view.territory_id
                    ? `Réglé sur « ${view.territory_id} ». Un atelier sur un autre territoire l'ignorera.`
                    : "Réglage sans territoire d'origine : il sera appliqué quel que soit le territoire de l'atelier. Recapturez-le pour le rattacher."}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
